/**
 * Converts a comma-separated string of key-value pairs into an object.
 *
 * @param {string} value - Input string in format "key1:value1, key2:value2"
 * @returns {Record<string, string>} An object with parsed key-value pairs
 * @throws {Error} If input string is malformed
 * @example
 * stringToObject('SKU:123, Color:Red');
 * // Returns { SKU: '123', Color: 'Red' }
 */
export const stringToObject = (value: string) => {
  if (!value) return {}

  const properties = value.split(', ')
  const obj: Record<string, any> = {}

  properties.forEach(function (property) {
    const tup = property.split(':')
    obj[tup[0]] = tup[1]
  })

  return obj
}

/**
 * Extracts the SKU (Stock Keeping Unit) from a size string.
 *
 * @param {string} size - Size string containing SKU information
 * @returns {string} The extracted SKU, trimmed of whitespace
 * @example
 * getSKUfromSize('SKU:ABC123, Size:M'); // Returns 'ABC123'
 * getSKUfromSize(''); // Returns ''
 */
export const getSKUfromSize = (size: string) => {
  return (stringToObject(size)?.SKU || '').trim()
}

/**
 * Extracts the file extension from a given URL.
 *
 * @param {string} url - The URL to extract file type from
 * @returns {string} The lowercase file extension, or empty string if no extension
 * @example
 * getFileTypeFromUrl('https://example.com/image.jpg'); // Returns 'jpg'
 * getFileTypeFromUrl('https://example.com/file'); // Returns ''
 */
export const getFileTypeFromUrl = (url: string) => {
  const regex = /(?:\.([^.]+))?$/
  const extension = regex.exec(url)?.at(1)
  return extension?.toLowerCase() ?? ''
}
